// ──────────────────────────────────────────────────────
// HeroCard — premium recommendation card (role='hero').
//
// QCARE-AI-DEV-FRONT-DARK-CARDS-V2 (2026-05-09): rebuilt to match the
// reference dark-premium design.
//
// Layout: CSS Grid `minmax(120px, 32%) 1fr` so the image panel sits on
// the LEFT (visually) with a stable, fixed-ish width and the text
// content fills the remaining space on the RIGHT. The grid container
// uses `direction: 'ltr'` so column-order is left→right; ONLY the text
// content panel re-applies `direction: 'rtl'` so Arabic flows naturally
// inside it. This avoids the prior `flex-direction: row` + `direction: rtl`
// trap that flipped the image to the wrong side or stretched it to fill
// 42% of the card width.
//
// Background: deep navy/teal gradient with a subtle cyan radial glow at
// the top-right corner. The hero shine ring/animation (3s linear infinite
// rotate) is preserved verbatim — only the inner card surface palette
// changed.
//
// Trust: ONE consolidated `<TrustBar>` at the bottom of the text column.
// Replaces the prior duplication where TrustBadge ("منتج أصلي 100%") was
// rendered in the header AND an inline "أصلي 100% — معتمد رسمياً" line
// was rendered in the body.
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import type { ProductSuggestion } from '@/lib/types';
import { TrustBar } from './TrustBar';
import { isProductOutOfStock, OUT_OF_STOCK_LABEL_AR } from './availability-badge';

const CYAN_PRIMARY = '#35E7F2';
const CYAN_SOFT = '#a8dce4';
const TEXT_PRIMARY = '#ffffff';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';
const TEXT_BODY = 'rgba(255,255,255,0.85)';
const SHELL_BORDER = 'rgba(80, 200, 200, 0.42)';
const DIVIDER = 'rgba(255,255,255,0.10)';

export function HeroCard({ product }: { product: ProductSuggestion }) {
    const ps = product;
    const bullets = (ps.reasons && ps.reasons.length > 0)
        ? ps.reasons.slice(0, 3)
        : (ps.shortReasonAr ? [ps.shortReasonAr] : []);
    const outOfStock = isProductOutOfStock(ps);

    // Hero badge text. The backend's per-card `ps.label` for hero turns
    // sometimes resolves to short brand-tier labels like "خيار رائج",
    // which doesn't match the reference card design. For role='hero'
    // we surface the canonical "أفضل overall ⭐" badge regardless of
    // the per-card label — backend's structural framing label outside
    // the card already says the same thing, so this is consistent.
    const heroBadgeIcon = '⭐';
    const heroBadgeText = 'أفضل overall';

    return (
        <>
            {/* Hero shine ring + 3s rotating animation — preserved verbatim
                from the prior implementation. @property registers the
                conic-gradient angle as an animatable typed CSS variable;
                ::before is the sharp 2.5px ring with a bright orbiting spot,
                ::after is the blurred outer halo trailing the same spot. */}
            <style>{`
                @property --shine-angle {
                    syntax: '<angle>';
                    initial-value: 0deg;
                    inherits: false;
                }
                .hero-shine-border {
                    position: relative;
                    border-radius: 18px;
                    padding: 2.5px;
                }
                .hero-shine-border::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    border-radius: 18px;
                    padding: 2.5px;
                    background: conic-gradient(
                        from var(--shine-angle, 0deg),
                        transparent 0%,
                        transparent 55%,
                        #a8dce4 62%,
                        #7ec2ce 70%,
                        #c1edf2 75%,
                        #7ec2ce 80%,
                        #a8dce4 88%,
                        transparent 95%,
                        transparent 100%
                    );
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    animation: hero-rotate-shine 3s linear infinite;
                    filter: blur(0.5px);
                    pointer-events: none;
                }
                .hero-shine-border::after {
                    content: '';
                    position: absolute;
                    inset: -3px;
                    border-radius: 21px;
                    padding: 5px;
                    background: conic-gradient(
                        from var(--shine-angle, 0deg),
                        transparent 0%,
                        transparent 60%,
                        rgba(126, 194, 206, 0.22) 68%,
                        rgba(126, 194, 206, 0.40) 75%,
                        rgba(126, 194, 206, 0.22) 82%,
                        transparent 90%,
                        transparent 100%
                    );
                    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
                    -webkit-mask-composite: xor;
                    mask-composite: exclude;
                    animation: hero-rotate-shine 3s linear infinite;
                    filter: blur(4px);
                    z-index: -1;
                    pointer-events: none;
                }
                @keyframes hero-rotate-shine {
                    0% { --shine-angle: 0deg; }
                    100% { --shine-angle: 360deg; }
                }
            `}</style>

            <div className="hero-shine-border">
                <Link
                    href={ps.href}
                    className="relative z-10 block overflow-hidden cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                    style={{
                        borderRadius: 16,
                        background:
                            'radial-gradient(circle at 85% 8%, rgba(80, 200, 200, 0.18), transparent 34%), '
                            + 'linear-gradient(145deg, #132633 0%, #0E1A24 45%, #071016 100%)',
                        border: `1px solid ${SHELL_BORDER}`,
                        boxShadow: '0 10px 28px rgba(0,0,0,0.50), inset 0 1px 0 rgba(255,255,255,0.04)',
                        textDecoration: 'none',
                    }}
                >
                    {/* Grid: image LEFT (col 1, stable width) + text RIGHT (col 2, flex).
                        QCARE-AI-DEV-FRONT-HERO-IMG-V4 (2026-05-09): reverted
                        the V3 column-width widening — restored the original
                        `minmax(120px, 32%)` so the image column does NOT
                        dominate the card. Hero-only height bump + top-align
                        from V3 are kept (the V3 fix that was actually
                        wanted). Budget/alternative cards in BudgetCard.tsx
                        still keep their own narrower grid template — no
                        shared style was ever changed. */}
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: 'minmax(120px, 32%) 1fr',
                            alignItems: 'stretch',
                            direction: 'ltr',
                            minHeight: 200,
                        }}
                    >
                        {/* Image panel — same width as before V3, slightly
                            taller, top-anchored.
                            QCARE-AI-DEV-FRONT-HERO-IMG-V4 (2026-05-09):
                            • Column width REVERTED to `minmax(120px, 32%)`
                              (the column above) so the panel's width matches
                              the pre-V3 layout the user wanted to keep.
                            • Outer wrapper KEEPS `alignItems: 'flex-start'`
                              (V3) so the panel sits at the TOP of the grid
                              cell, level with the badge area.
                            • Inner panel KEEPS an explicit clamp height so
                              the panel is slightly TALLER than a perfect
                              square at the same width — but the clamp is
                              tightened from V3's `clamp(180px, 34vw, 280px)`
                              to `clamp(160px, 30vw, 240px)` to keep the
                              vertical extension moderate (the bigger V3
                              clamp was tuned for a wider column that's no
                              longer in play).
                            • `object-fit: contain` on the <img> preserved so
                              packaging photography never stretches or crops.
                            • Budget/alternative cards are NOT affected — they
                              live in BudgetCard.tsx with their own smaller
                              `aspect-ratio: 1 / 1` panel. */}
                        <div
                            style={{
                                padding: 14,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'center',
                            }}
                        >
                            <div
                                style={{
                                    background: '#F3F7F8',
                                    borderRadius: 22,
                                    padding: 18,
                                    width: '100%',
                                    height: 'clamp(160px, 30vw, 240px)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: 'inset 0 0 0 1px rgba(80,200,200,0.12)',
                                }}
                            >
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src={ps.imageUrl}
                                    alt={ps.nameAr}
                                    className="q-card-image-fade"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '100%',
                                        objectFit: 'contain',
                                    }}
                                    onLoad={(e) => {
                                        const el = e.target as HTMLImageElement;
                                        if (el.dataset.fallback === '1') return;
                                        el.style.opacity = '1';
                                    }}
                                    onError={(e) => {
                                        const el = e.target as HTMLImageElement;
                                        if (el.dataset.fallback === '1') return;
                                        el.dataset.fallback = '1';
                                        el.src = '/branding/qio-logo.jpeg';
                                        el.style.opacity = '0.55';
                                    }}
                                />
                            </div>
                        </div>

                        {/* Text panel — RTL flow. Header row (badge + OOS),
                            title/brand/price block, reasons block, single
                            consolidated TrustBar at the bottom. */}
                        <div
                            style={{
                                direction: 'rtl',
                                padding: '14px 16px 14px 8px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                minWidth: 0,
                                gap: 10,
                            }}
                        >
                            {/* Header row — hero badge + optional OOS pill. */}
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, flexWrap: 'wrap' }}>
                                <span
                                    // QCARE-AI-DEV-FRONT-BADGE-GLASS (2026-05-09):
                                    // glass-style badge — translucent cyan
                                    // gradient, semi-opaque cyan border, soft
                                    // outer glow + subtle inset highlight,
                                    // backdrop blur where supported. Replaces
                                    // the prior solid cyan gradient that read
                                    // as "filled chip" instead of "premium
                                    // glass pill". Geometry (padding / radius
                                    // / fontSize / icon-text gap) unchanged.
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 4,
                                        padding: '4px 10px',
                                        borderRadius: 999,
                                        fontSize: 11,
                                        fontWeight: 700,
                                        letterSpacing: '0.02em',
                                        color: '#BDFBFF',
                                        background: 'linear-gradient(135deg, rgba(80, 200, 200, 0.22), rgba(0, 255, 255, 0.10))',
                                        border: '1px solid rgba(80, 200, 200, 0.55)',
                                        boxShadow: '0 0 18px rgba(80, 200, 200, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.10)',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <span>{heroBadgeText}</span>
                                    <span aria-hidden>{heroBadgeIcon}</span>
                                </span>
                                {outOfStock && (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 4,
                                            padding: '2px 6px',
                                            borderRadius: 6,
                                            fontSize: 10,
                                            fontWeight: 600,
                                            background: 'rgba(180,35,24,0.15)',
                                            color: '#fca5a5',
                                            border: '1px solid rgba(252,165,165,0.40)',
                                        }}
                                        aria-label={OUT_OF_STOCK_LABEL_AR}
                                    >
                                        {OUT_OF_STOCK_LABEL_AR}
                                    </span>
                                )}
                            </div>

                            {/* Title + brand + price block. */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
                                <div
                                    style={{
                                        color: TEXT_PRIMARY,
                                        fontWeight: 600,
                                        fontSize: 17,
                                        lineHeight: 1.35,
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {ps.nameAr}
                                </div>
                                {ps.brandAr && (
                                    <div style={{ color: TEXT_MUTED, fontSize: 12, fontWeight: 500 }}>
                                        {ps.brandAr}
                                    </div>
                                )}
                                {ps.price != null && ps.price > 0 && (
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 2 }}>
                                        <span
                                            style={{
                                                fontSize: 28,
                                                fontWeight: 700,
                                                color: CYAN_PRIMARY,
                                                letterSpacing: '0.01em',
                                                textShadow: '0 1px 8px rgba(53,231,242,0.30)',
                                                lineHeight: 1,
                                            }}
                                        >
                                            {ps.price.toFixed(2)}
                                        </span>
                                        <span style={{ fontSize: 13, color: CYAN_SOFT, fontWeight: 500 }}>ريال</span>
                                    </div>
                                )}
                            </div>

                            {/* Reasons block — only if backend supplied any. */}
                            {bullets.length > 0 && (
                                <div>
                                    <div style={{ borderTop: `1px solid ${DIVIDER}`, marginBottom: 8 }} />
                                    <div style={{ color: CYAN_SOFT, fontSize: 11, fontWeight: 700, marginBottom: 4 }}>
                                        ليش الأفضل:
                                    </div>
                                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {bullets.map((b, i) => (
                                            <li
                                                key={i}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                    fontSize: 12,
                                                    lineHeight: 1.5,
                                                    color: TEXT_BODY,
                                                }}
                                            >
                                                <span
                                                    aria-hidden
                                                    style={{
                                                        flexShrink: 0,
                                                        width: 7,
                                                        height: 7,
                                                        borderRadius: 999,
                                                        background: CYAN_PRIMARY,
                                                        boxShadow: '0 0 6px rgba(53,231,242,0.55)',
                                                    }}
                                                />
                                                <span>{b}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* QCARE-AI-DEV-FRONT-TRUSTBAR-FULLWIDTH (2026-05-09):
                            trust bar moved OUT of the text column and INTO a
                            full-width grid footer row. `gridColumn: '1 / -1'`
                            spans both image (col 1) + text (col 2) so the
                            green emerald bar stretches the full inner card
                            width — no more empty gap under the image column.
                            Inner padding mirrors the card's outer chrome
                            (14 px sides, 14 px bottom) so the bar respects
                            the card's edge gutters. `direction: 'rtl'` is
                            re-applied here so the bar's RTL alignment stays
                            clean even though the outer Grid container is
                            `direction: 'ltr'`. */}
                        <div
                            style={{
                                gridColumn: '1 / -1',
                                padding: '0 14px 14px 14px',
                                direction: 'rtl',
                            }}
                        >
                            <TrustBar size="lg" />
                        </div>
                    </div>
                </Link>
            </div>
        </>
    );
}
